import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  GetApp as ExportIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import api from '../api/api';

const ALLOWED_SORT_FIELDS = ['id','timestamp','user_id','tenant_id','entity','action'];
const ALLOWED_SORT_ORDERS = ['asc','desc'];

export default function LogsPage() {
  const [filters, setFilters] = useState({
    user_id: '',
    tenant_id: '',
    entity: '',
    action: '',
    method: '',
    path: '',
    from: '',
    to: '',
    sortField: 'timestamp',
    sortOrder: 'desc'
  });
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [totalPages, setTotalPages] = useState(1);

  const [openDialog, setOpenDialog] = useState(false);
  const [dialogData, setDialogData] = useState(null);
  const [isEdit, setIsEdit] = useState(false);

  // Construye params sin incluir valores vacíos
  const buildParams = (extra = {}) => {
    const params = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '' && value !== null) params[key] = value;
    });
    params.page = page;
    params.limit = limit;
    params.sort = `${filters.sortField}:${filters.sortOrder}`;
    return { ...params, ...extra };
  };

  const fetchLogs = async () => {
    try {
      const params = buildParams();
      const res = await api.get('/logs', { params });
      setLogs(res.data.data);
      setTotalPages(Math.ceil(res.data.total / res.data.limit));
    } catch (err) {
      console.error('Error fetching logs:', err);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [filters, page]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const handleExport = (format) => {
    const exportParams = buildParams({ format });
    const queryString = new URLSearchParams(exportParams).toString();
    // Usa la baseURL de axios para componer la ruta completa
    const base = api.defaults.baseURL?.replace(/\/$/, '') || '';
    window.open(`${base}/logs/export?${queryString}`, '_blank');
  };

  const handleOpenCreate = () => {
    setDialogData({
      user_id: '',
      tenant_id: '',
      ip_address: '',
      method: '',
      path: '',
      entity: '',
      entity_id: '',
      action: '',
      details: ''
    });
    setIsEdit(false);
    setOpenDialog(true);
  };

  const handleOpenEdit = (log) => {
    setDialogData({ ...log, details: log.details || '' });
    setIsEdit(true);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => setOpenDialog(false);

  const handleSubmitDialog = async () => {
    try {
      if (isEdit) {
        await api.put(`/logs/${dialogData.id}`, dialogData);
      } else {
        await api.post('/logs', dialogData);
      }
      fetchLogs();
      setOpenDialog(false);
    } catch (err) {
      console.error('Error saving log:', err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(`¿Eliminar log #${id}?`)) {
      try {
        await api.delete(`/logs/${id}`);
        fetchLogs();
      } catch (err) {
        console.error('Error deleting log:', err);
      }
    }
  };

  return (
    <Box p={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Logs de Auditoría</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          Nuevo Log
        </Button>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          {/* Campos de filtro */}
          {['user_id','tenant_id','entity','action','method','path'].map(field => (
            <TextField
              key={field}
              label={field.replace('_', ' ').toUpperCase()}
              name={field}
              value={filters[field]}
              onChange={handleFilterChange}
              size="small"
            />
          ))}
          <TextField
            label="Desde"
            name="from"
            type="datetime-local"
            value={filters.from}
            onChange={handleFilterChange}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Hasta"
            name="to"
            type="datetime-local"
            value={filters.to}
            onChange={handleFilterChange}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <FormControl size="small">
            <InputLabel>Ordenar por</InputLabel>
            <Select
              label="Ordenar por"
              name="sortField"
              value={filters.sortField}
              onChange={handleFilterChange}
            >
              {ALLOWED_SORT_FIELDS.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>Orden</InputLabel>
            <Select
              label="Orden"
              name="sortOrder"
              value={filters.sortOrder}
              onChange={handleFilterChange}
            >
              {ALLOWED_SORT_ORDERS.map(o => <MenuItem key={o} value={o}>{o.toUpperCase()}</MenuItem>)}
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<SearchIcon />} onClick={fetchLogs}>
            Filtrar
          </Button>
        </Stack>
        <Box mt={2}>
          <Button variant="outlined" startIcon={<ExportIcon />} onClick={() => handleExport('json')}>
            Exportar JSON
          </Button>
          <Button variant="outlined" startIcon={<ExportIcon />} onClick={() => handleExport('csv')} sx={{ ml: 1 }}>
            Exportar CSV
          </Button>
        </Box>
      </Paper>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Timestamp</TableCell>
                <TableCell>Usuario</TableCell>
                <TableCell>Tenant</TableCell>
                <TableCell>IP</TableCell>
                <TableCell>Método</TableCell>
                <TableCell>Ruta</TableCell>
                <TableCell>Ent.</TableCell>
                <TableCell>Entidad ID</TableCell>
                <TableCell>Acción</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map(log => (
                <TableRow key={log.id} hover>
                  <TableCell>{log.id}</TableCell>
                  <TableCell>{new Date(log.timestamp).toLocaleString()}</TableCell>
                  <TableCell>{log.user_id}</TableCell>
                  <TableCell>{log.tenant_id}</TableCell>
                  <TableCell>{log.ip_address}</TableCell>
                  <TableCell>{log.method}</TableCell>
                  <TableCell>{log.path}</TableCell>
                  <TableCell>{log.entity}</TableCell>
                  <TableCell>{log.entity_id}</TableCell>
                  <TableCell>{log.action}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpenEdit(log)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(log.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box display="flex" justifyContent="center" my={2}>
          <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} />
        </Box>
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>{isEdit ? 'Editar Log' : 'Crear Log'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {[
              { label: 'Usuario ID', name: 'user_id', type: 'number' },
              { label: 'Tenant ID', name: 'tenant_id', type: 'number' },
              { label: 'IP Address', name: 'ip_address' },
              { label: 'Método', name: 'method' },
              { label: 'Ruta', name: 'path' },
              { label: 'Entidad', name: 'entity' },
              { label: 'Entidad ID', name: 'entity_id', type: 'number' },
              { label: 'Acción', name: 'action' },
              { label: 'Detalles (JSON)', name: 'details', multiline: true, rows: 3 }
            ].map(field => (
              <TextField
                key={field.name}
                label={field.label}
                name={field.name}
                type={field.type || 'text'}
                multiline={field.multiline || false}
                rows={field.rows || 1}
                value={dialogData?.[field.name] || ''}
                onChange={e => setDialogData(prev => ({ ...prev, [field.name]: e.target.value }))}
                fullWidth
              />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button variant="contained" onClick={handleSubmitDialog}>
            {isEdit ? 'Guardar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
